/**
 * Turning host lifecycle signals into relay recovery.
 *
 * Reconnection is otherwise driven entirely by socket close events and a backoff
 * schedule, and there is one situation where both are wrong at once: the machine
 * was asleep, or the network was gone, or the tab was in the background for an
 * hour. Every relay's backoff is then sitting at its 30-second cap having failed
 * repeatedly against an interface that was not there, and any socket the peer gave
 * up on never reported closing — from this side it is still `OPEN`, and nothing
 * will ever arrive on it again.
 *
 * The host knows when that has happened; the pool does not. So the host says so.
 *
 * Deliberately structural rather than DOM-typed: `core` is headless, and the same
 * wiring has to work from a Tauri shell and from `apps/cli`, neither of which has
 * a `document`. A caller passes whatever emits its own equivalents.
 */

import type { RelayPool, Unsubscribe } from "../contracts";

/** The `addEventListener`/`removeEventListener` pair, and nothing else. */
export interface WakeEventTarget {
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

/** A target that also reports whether the surface is currently visible. */
export interface WakeVisibilityTarget extends WakeEventTarget {
  readonly visibilityState?: string;
}

/**
 * Minimum gap between wakes.
 *
 * A wake re-dials every relay that is not demonstrably live, so it must not be
 * driven directly by an event a user can produce at will — switching tabs twice a
 * second would hammer a dead relay with handshakes. Five seconds is short enough
 * to be invisible after a real sleep and long enough that alt-tabbing is free.
 */
const DEFAULT_MIN_INTERVAL_MS = 5_000;

/** Construction options for {@link startWakeWatch}. */
export interface WakeWatchOptions {
  /** The pool to restore. Only `wake` is used. */
  readonly pool: Pick<RelayPool, "wake">;
  /** Emits `online`. `window` in a browser. */
  readonly connectivity?: WakeEventTarget;
  /** Emits `visibilitychange`. `document` in a browser. */
  readonly visibility?: WakeVisibilityTarget;
  /** Minimum gap between wakes. Default 5000ms. */
  readonly minIntervalMs?: number;
  readonly now?: () => number;
}

/**
 * Wakes `pool` when the surface becomes visible again or connectivity returns.
 *
 * Returns an unsubscribe. Absent targets are simply not listened to, so a host
 * that has one and not the other needs no branch of its own.
 */
export function startWakeWatch(options: WakeWatchOptions): Unsubscribe {
  const { pool, connectivity, visibility } = options;
  const now = options.now ?? Date.now;
  const minInterval = options.minIntervalMs ?? DEFAULT_MIN_INTERVAL_MS;
  let lastWakeAt = Number.NEGATIVE_INFINITY;

  const wake = (): void => {
    const at = now();
    if (at - lastWakeAt < minInterval) return;
    lastWakeAt = at;
    pool.wake();
  };

  const onVisibility = (): void => {
    // Going *away* is not a wake — and the two arrive through one event.
    if (visibility?.visibilityState === "hidden") return;
    wake();
  };

  connectivity?.addEventListener("online", wake);
  visibility?.addEventListener("visibilitychange", onVisibility);

  return () => {
    connectivity?.removeEventListener("online", wake);
    visibility?.removeEventListener("visibilitychange", onVisibility);
  };
}
